import { memo, useMemo } from 'react'
import { usePathname } from 'next/navigation'

import { NavMenuCategory } from 'modules/products'
import { addTestId } from 'shared/utils/tests'

import CategoryNavigationTreeItem from './CategoriesNavigationTreeItem'

import styles from './CategoriesNavigationTreeColumn.module.scss'

interface CategoriesNavigationTreeColumnProps {
  index: number
  isLastColumn: boolean
  categoriesTreeSelectionState: (Partial<NavMenuCategory> | null)[]
  categories: NavMenuCategory[]
  handleCategoryFocus: (category: NavMenuCategory, level: number) => void
  handleKeyDown: (event: React.KeyboardEvent, columnIndex: number) => void
}

const CategoriesNavigationTreeColumn = memo(
  ({
    index,
    isLastColumn,
    categoriesTreeSelectionState,
    categories,
    handleCategoryFocus,
    handleKeyDown
  }: CategoriesNavigationTreeColumnProps) => {
    const pathname = usePathname()
    const focusedCategory = useMemo(
      () => categoriesTreeSelectionState?.[index] as NavMenuCategory | null,
      [categoriesTreeSelectionState, index]
    )

    const prevCategory = useMemo(
      () => categoriesTreeSelectionState?.[index - 1] as NavMenuCategory | null,
      [categoriesTreeSelectionState, index]
    )

    const siblingCategories = useMemo(
      () => prevCategory?.subCategories ?? [],
      [prevCategory]
    )

    return (
      <div className={styles.columnWrapper} tabIndex={-1}>
        <ul
          className={styles.column}
          id={`category-menu-column-${index + 1}`}
          key={index}
          onKeyDown={event => handleKeyDown(event, index)}
          tabIndex={index === 0 ? 0 : -1}
          aria-label={
            index === 0
              ? 'Główne kategorie'
              : `Podkategorie dla kategorii ${categoriesTreeSelectionState[index - 1]?.name}`
          }
          role={index === 0 ? 'tree' : 'group'}
          {...addTestId(`category-menu-column-${index + 1}`)}
        >
          {index === 0 &&
            categories?.map((category: NavMenuCategory) => {
              if (!category.isVisible) {
                return null
              }

              return (
                <CategoryNavigationTreeItem
                  key={category.id}
                  category={category}
                  focusedCategory={focusedCategory}
                  level={index + 1}
                  href={category.totalSlug}
                  pathname={pathname}
                  onFocusCategory={handleCategoryFocus}
                />
              )
            })}
          {index > 0 &&
            !isLastColumn &&
            siblingCategories.map((category: NavMenuCategory) => (
              <CategoryNavigationTreeItem
                key={category.id}
                category={category}
                focusedCategory={focusedCategory}
                level={index + 1}
                href={category.totalSlug}
                pathname={pathname}
                onFocusCategory={handleCategoryFocus}
              />
            ))}
          {index > 0 &&
            isLastColumn &&
            siblingCategories.map((category: NavMenuCategory) => (
              <CategoryNavigationTreeItem
                key={category.id}
                category={category}
                focusedCategory={focusedCategory}
                level={index + 1}
                href={category.totalSlug}
                pathname={pathname}
                onFocusCategory={handleCategoryFocus}
                showChevron={false}
              />
            ))}
        </ul>
      </div>
    )
  }
)

export default CategoriesNavigationTreeColumn
